/**
 * HouseFirstPinDialog — shown the first time anyone tries to "enter" a
 * newly-created house that still has no PIN. Forces the person doing
 * the visit to set a 6+ digit house PIN (same rules as HousesPanel)
 * before they can step inside.
 *
 * Success path: PIN saved server-side AND cached locally via
 * `setHouseUnlock` so the same parent isn't re-prompted a second later
 * when they tap a member.
 */
import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { setHouseUnlock } from "@/lib/houseLock";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";

const isWeakPin = (pin) => {
  if (!/^\d{6,}$/.test(pin)) return true;
  if (/^(\d)\1+$/.test(pin)) return true; // 111111, 000000
  // Sequential up or down (6+ digits)
  const up = "0123456789".includes(pin);
  const down = "9876543210".includes(pin);
  return up || down;
};

export default function HouseFirstPinDialog({ house, onDone, onClose }) {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const p = newPin.trim();
    if (!/^\d{6,}$/.test(p)) {
      toast.error("House PIN must be at least 6 digits");
      return;
    }
    if (isWeakPin(p)) {
      toast.error("Pick something less obvious (no 123456 or 000000)");
      return;
    }
    if (p !== confirmPin.trim()) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await api.setHousePin(house.id, { new_pin: p });
      setHouseUnlock(house.id, p);
      toast.success(`${house.name} locked · PIN set`);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="house-first-pin-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[color:var(--bg)] md:rounded-[18px] rounded-t-[24px] w-full md:max-w-sm p-5 md:p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--bg-soft)] flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="label-tiny">Set a house PIN</div>
            <h2 className="font-display-italic text-2xl leading-tight mt-0.5">
              {house.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            aria-label="Close"
            data-testid="house-first-pin-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <p className="text-[14px] leading-snug text-[color:var(--ink-2)]">
          Pick a PIN only the people living in {house.name} should know.
          Other houses in the family need this to peek inside.
          Minimum 6 digits.
        </p>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          autoComplete="off"
          placeholder="New house PIN"
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
          className="ff-input text-center text-2xl tabular"
          data-testid="house-first-pin-input"
        />
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Confirm PIN"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
          className="ff-input text-center text-2xl tabular"
          data-testid="house-first-pin-confirm"
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="ff-btn"
            data-testid="house-first-pin-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="ff-btn ff-btn-primary"
            data-testid="house-first-pin-submit"
          >
            {busy ? "Saving…" : "Lock it"}
          </button>
        </div>
      </form>
    </div>
  );
}
